import { type ScheduleEntry } from "../types";

interface ScheduleSectionProps {
  title: string;
  icon: string;
  iconColor: string;
  entries: ScheduleEntry[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  onChange: (i: number, field: "day" | "time", value: string) => void;
  dayPlaceholder?: string;
}

const DAY_OPTIONS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
  "Lundi - Vendredi",
  "Lundi - Samedi",
  "Mercredi & Vendredi",
  "Mercredi & Samedi",
  "Jeudi & Samedi",
  "Vendredi & Samedi",
  "Mardi & Jeudi",
  "Samedi & Dimanche",
  "Lundi, Mercredi & Vendredi",
  "Mardi, Jeudi & Samedi",
  "Mercredi, Vendredi & Dimanche",
  "Quotidien",
  "Tous les jours",
];

function ScheduleSection({
  title,
  icon,
  iconColor,
  entries,
  onAdd,
  onRemove,
  onChange,
  dayPlaceholder = "ex: Dimanche",
}: ScheduleSectionProps) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "rgba(13,27,94,0.08)" }}>
            <i className={`${icon} text-base`} style={{ color: iconColor }} />
          </div>
          <p className="font-bold text-stone-700 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="w-7 h-7 flex items-center justify-center rounded-full bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
        >
          <i className="ri-add-line text-green-600 text-base" />
        </button>
      </div>

      {/* Rows */}
      <div className="px-4 py-3 flex flex-col gap-3">
        {entries.map((entry, i) => (
          <div key={i} className="flex items-center gap-2">
            {/* Day */}
            <div className="flex-1 relative">
              <select
                value={entry.day}
                onChange={(e) => onChange(i, "day", e.target.value)}
                className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-xs text-stone-700 outline-none appearance-none cursor-pointer"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                <option value="">{dayPlaceholder}</option>
                {DAY_OPTIONS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-stone-400 text-sm" />
              </div>
            </div>
            {/* Time */}
            <input
              type="text"
              value={entry.time}
              onChange={(e) => onChange(i, "time", e.target.value)}
              placeholder="07h00 ou 07h00 & 09h00"
              className="w-36 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-xs text-stone-700 outline-none"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            />
            {/* Remove */}
            {entries.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-red-50 cursor-pointer hover:bg-red-100 transition-colors flex-shrink-0"
              >
                <i className="ri-delete-bin-line text-red-400 text-sm" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface Props {
  massSchedule: ScheduleEntry[];
  confessionSchedule: ScheduleEntry[];
  officeHours: ScheduleEntry[];
  onMassChange: (i: number, field: "day" | "time", value: string) => void;
  onMassAdd: () => void;
  onMassRemove: (i: number) => void;
  onConfChange: (i: number, field: "day" | "time", value: string) => void;
  onConfAdd: () => void;
  onConfRemove: (i: number) => void;
  onOfficeChange: (i: number, field: "day" | "time", value: string) => void;
  onOfficeAdd: () => void;
  onOfficeRemove: (i: number) => void;
}

export default function StepSchedules({
  massSchedule,
  confessionSchedule,
  officeHours,
  onMassChange, onMassAdd, onMassRemove,
  onConfChange, onConfAdd, onConfRemove,
  onOfficeChange, onOfficeAdd, onOfficeRemove,
}: Props) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-stone-400 leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
        Ajoutez les horaires des messes, des confessions et des heures de bureau de la paroisse.
        Vous pouvez ajouter plusieurs créneaux par section.
      </p>

      <ScheduleSection
        title="Horaires des Messes"
        icon="ri-sun-line"
        iconColor="#d97706"
        entries={massSchedule}
        onAdd={onMassAdd}
        onRemove={onMassRemove}
        onChange={onMassChange}
        dayPlaceholder="Choisir le(s) jour(s)"
      />

      <ScheduleSection
        title="Confessions"
        icon="ri-discuss-line"
        iconColor="#0D1B5E"
        entries={confessionSchedule}
        onAdd={onConfAdd}
        onRemove={onConfRemove}
        onChange={onConfChange}
        dayPlaceholder="Choisir le(s) jour(s)"
      />

      <ScheduleSection
        title="Heures de Bureau"
        icon="ri-building-line"
        iconColor="#57534e"
        entries={officeHours}
        onAdd={onOfficeAdd}
        onRemove={onOfficeRemove}
        onChange={onOfficeChange}
        dayPlaceholder="Choisir le(s) jour(s)"
      />
    </div>
  );
}
